<template>
  <div class="statistics-page">
    <header class="page-header">
      <div class="page-header__top">
        <h1 class="page-header__title">iWin11 交易统计</h1>
        <button type="button" class="refresh-btn" :disabled="isRefreshing" @click="refreshData" title="重新拉取实时数据">
          <svg class="refresh-btn__icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
            <path d="M21 12a9 9 0 1 1-2.64-6.36"></path>
            <path d="M21 3v6h-6"></path>
          </svg>
          <span>刷新</span>
        </button>
      </div>
      <p class="data-status" :class="'data-status--' + statusType" role="status" aria-live="polite">
        <span class="data-status__dot"></span>{{ statusText }}
      </p>
    </header>

    <section class="kpi-section" aria-label="核心指标概览">
      <article class="kpi-card" v-for="kpi in kpiCards" :key="kpi.key">
        <div class="kpi-card__label">{{ kpi.label }}</div>
        <div class="kpi-card__value">{{ kpi.todayText }}</div>
        <div class="kpi-card__summary">
          <span class="kpi-card__summary-prefix">近 30 天</span>
          <span class="kpi-card__summary-value">{{ kpi.totalText }}</span>
        </div>
      </article>
    </section>

    <section class="card" aria-label="趋势图">
      <div class="card__header">
        <h2 class="card__title">{{ apiData.today.date }}</h2>
        <div class="chart-tabs" role="tablist">
          <button v-for="m in metricTabs" :key="m.key"
                  class="chart-tabs__btn"
                  :class="{ 'chart-tabs__btn--active': currentMetric === m.key }"
                  :aria-selected="currentMetric === m.key ? 'true' : 'false'"
                  @click="switchChart(m.key)" role="tab">
            {{ m.label }}
          </button>
        </div>
      </div>
      <div class="chart-container" ref="chart"></div>
    </section>

    <section class="card" aria-label="每日明细">
      <table class="data-table">
        <thead>
        <tr>
          <th scope="col" :class="sortClass('date')" :aria-sort="ariaSort('date')" @click="applySort('date')" title="点击按日期排序">日期<span class="sort-indicator"></span></th>
          <th scope="col" :class="sortClass('num')" :aria-sort="ariaSort('num')" @click="applySort('num')" title="点击按订单数排序">订单数<span class="sort-indicator"></span></th>
          <th scope="col" :class="sortClass('pay_num')" :aria-sort="ariaSort('pay_num')" @click="applySort('pay_num')" title="点击按支付笔数排序"><span class="th-full">支付笔数</span><span class="th-short">笔数</span><span class="sort-indicator"></span></th>
          <th scope="col" :class="sortClass('rate')" :aria-sort="ariaSort('rate')" @click="applySort('rate')" title="点击按支付转化率排序"><span class="th-full">支付转化率</span><span class="th-short">转化率</span><span class="sort-indicator"></span></th>
          <th scope="col" :class="sortClass('free')" :aria-sort="ariaSort('free')" @click="applySort('free')" title="点击按支付金额排序"><span class="th-full">支付金额</span><span class="th-short">金额</span><span class="sort-indicator"></span></th>
        </tr>
        </thead>
        <tbody>
        <template v-for="(row, i) in sortedList">
          <tr  :key="row.date" class="daily-row"
               :class="{ 'daily-row--expanded': expandedDate === row.date }"
               :style="{ animationDelay: (i * 0.02) + 's' }"
               :title="row.date" @click="toggleRow(row.date)">
            <td class="daily-row__date">
              <span class="daily-row__date-inner">
                <span class="expand-icon" aria-hidden="true"></span>
                <span><span class="date-year">{{ row.date.slice(0, 5) }}</span>{{ row.date.slice(5) }}</span>
              </span>
            </td>
            <td>{{ row.num }}</td>
            <td>{{ row.pay_num }}</td>
            <td class="daily-row__rate" :class="rateCellClass(row)">{{ rateText(row) }}</td>
            <td>{{ formatMoney(row.free) }}</td>
          </tr>
          <tr class="detail-row" v-show="expandedDate === row.date">
            <td colspan="5">
              <div class="detail-panel">
                <p class="detail-empty" v-if="!detailState[row.date] || detailState[row.date].status === 'idle'">点击左侧日期展开产品明细</p>
                <p class="detail-loading" v-else-if="detailState[row.date].status === 'loading'"><span class="spinner"></span>正在加载产品明细…</p>
                <p class="detail-error" v-else-if="detailState[row.date].status === 'error'">
                  加载失败：{{ detailState[row.date].error }}
                  <button type="button" class="detail-retry" @click="loadDayDetail(row.date)">重试</button>
                </p>
                <div class="detail-list" v-else>
                  <div class="detail-item" v-for="item in detailState[row.date].items" :key="item.name">
                    <div class="detail-item__name">{{ item.name }}</div>
                    <div class="detail-item__grid">
                      <div class="detail-cell">
                        <span class="detail-cell__label">订单数</span>
                        <span class="detail-cell__value">{{ item.num }}</span>
                      </div>
                      <div class="detail-cell">
                        <span class="detail-cell__label">支付笔数</span>
                        <span class="detail-cell__value">{{ item.pay_num }}</span>
                      </div>
                      <div class="detail-cell" :class="'detail-cell--rate--' + rateLevel(item)">
                        <span class="detail-cell__label">支付转化率</span>
                        <span class="detail-cell__value">{{ rateText(item) }}</span>
                      </div>
                      <div class="detail-cell">
                        <span class="detail-cell__label">支付金额</span>
                        <span class="detail-cell__value">{{ formatMoney(item.free) }}</span>
                      </div>
                    </div>
                    <div class="detail-pay">
                      <div class="detail-pay__row">
                        <span class="detail-pay__channel">微信</span>
                        <span class="detail-pay__value">{{ numOf(item.wx, 'pay_num') }}笔</span>
                        <span class="detail-pay__value">¥{{ formatMoney(numOf(item.wx, 'free')) }}</span>
                      </div>
                      <div class="detail-pay__row">
                        <span class="detail-pay__channel">支付宝</span>
                        <span class="detail-pay__value">{{ numOf(item.ali, 'pay_num') }}笔</span>
                        <span class="detail-pay__value">¥{{ formatMoney(numOf(item.ali, 'free')) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </template>
        </tbody>
      </table>
    </section>

    <footer class="page-footer">仅显示近 30 天数据</footer>
  </div>

</template>

<script>
import * as echarts from 'echarts';

const FALLBACK = {"total":{"num":"8335","free":"1762.66","pay_num":"954","wx":{"pay_num":"1770"},"ali":{"pay_num":"138"}},"today":{"date":"2026-09-04","num":"381","free":"103","pay_num":"51","wx":{"pay_num":"49"},"ali":{"pay_num":"2"}},"list":[{"date":"2026-09-04","num":"381","free":"103","pay_num":"51","wx":{"pay_num":"49"},"ali":{"pay_num":"2"}},{"date":"2026-09-03","num":"328","free":"91","pay_num":"47","wx":{"pay_num":"46"},"ali":{"pay_num":"1"}},{"date":"2026-09-02","num":"273","free":"44","pay_num":"22","wx":{"pay_num":"21"},"ali":{"pay_num":"1"}},{"date":"2026-09-01","num":"332","free":"81.25","pay_num":"38","wx":{"pay_num":"34"},"ali":{"pay_num":"4"}},{"date":"2026-08-31","num":"284","free":"72","pay_num":"41","wx":{"pay_num":"36"},"ali":{"pay_num":"5"}},{"date":"2026-08-30","num":"259","free":"71","pay_num":"34","wx":{"pay_num":"31"},"ali":{"pay_num":"3"}},{"date":"2026-08-29","num":"305","free":"52","pay_num":"26","wx":{"pay_num":"24"},"ali":{"pay_num":"2"}},{"date":"2026-08-28","num":"369","free":"72","pay_num":"36","wx":{"pay_num":"33"},"ali":{"pay_num":"3"}},{"date":"2026-08-27","num":"349","free":"72","pay_num":"37","wx":{"pay_num":"35"},"ali":{"pay_num":"2"}},{"date":"2026-08-26","num":"278","free":"53.75","pay_num":"29","wx":{"pay_num":"27"},"ali":{"pay_num":"2"}},{"date":"2026-08-25","num":"267","free":"42.5","pay_num":"23","wx":{"pay_num":"23"},"ali":{"pay_num":0}},{"date":"2026-08-24","num":"247","free":"50.26","pay_num":"28","wx":{"pay_num":"27"},"ali":{"pay_num":"1"}},{"date":"2026-08-22","num":"271","free":"34","pay_num":"17","wx":{"pay_num":"15"},"ali":{"pay_num":"2"}},{"date":"2026-08-21","num":"270","free":"55","pay_num":"29","wx":{"pay_num":"27"},"ali":{"pay_num":"2"}},{"date":"2026-08-20","num":"272","free":"53.75","pay_num":"25","wx":{"pay_num":"24"},"ali":{"pay_num":"1"}},{"date":"2026-08-19","num":"260","free":"43.25","pay_num":"23","wx":{"pay_num":"22"},"ali":{"pay_num":"1"}},{"date":"2026-08-18","num":"310","free":"82.5","pay_num":"40","wx":{"pay_num":"37"},"ali":{"pay_num":"3"}},{"date":"2026-08-17","num":"250","free":"56.25","pay_num":"28","wx":{"pay_num":"26"},"ali":{"pay_num":"2"}},{"date":"2026-08-16","num":"230","free":"45","pay_num":"26","wx":{"pay_num":"24"},"ali":{"pay_num":"2"}},{"date":"2026-08-15","num":"318","free":"82.5","pay_num":"42","wx":{"pay_num":"39"},"ali":{"pay_num":"3"}},{"date":"2026-08-14","num":"347","free":"74","pay_num":"39","wx":{"pay_num":"36"},"ali":{"pay_num":"3"}},{"date":"2026-08-13","num":"316","free":"45.05","pay_num":"27","wx":{"pay_num":"25"},"ali":{"pay_num":"2"}},{"date":"2026-08-12","num":"225","free":"43.2","pay_num":"31","wx":{"pay_num":"29"},"ali":{"pay_num":"2"}},{"date":"2026-08-11","num":"282","free":"70.2","pay_num":"44","wx":{"pay_num":"41"},"ali":{"pay_num":"3"}},{"date":"2026-08-10","num":"266","free":"38.8","pay_num":"26","wx":{"pay_num":"24"},"ali":{"pay_num":"2"}},{"date":"2026-08-09","num":"270","free":"81.6","pay_num":"47","wx":{"pay_num":"45"},"ali":{"pay_num":"2"}},{"date":"2026-08-08","num":"278","free":"64.8","pay_num":"41","wx":{"pay_num":"38"},"ali":{"pay_num":"3"}},{"date":"2026-08-07","num":"264","free":"35.8","pay_num":"23","wx":{"pay_num":"22"},"ali":{"pay_num":"1"}},{"date":"2026-08-06","num":"234","free":"52.2","pay_num":"34","wx":{"pay_num":"32"},"ali":{"pay_num":"2"}}]};
const DAY_DETAILS = {"2026-09-04":[{"name":"Win10_Pro_x64_2512.GHO","num":"136","free":"48","pay_num":"16","wx":{"num":"135","free":"45","pay_num":"15"},"ali":{"num":"1","free":"3","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"118","free":"31.5","pay_num":"21","wx":{"num":"118","free":"31.5","pay_num":"21"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"50","free":"10.5","pay_num":"6","wx":{"num":"50","free":"10.5","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"21","free":"4","pay_num":"2","wx":{"num":"21","free":"4","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"34","free":"3.75","pay_num":"3","wx":{"num":"34","free":"3.75","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"16","free":"3.5","pay_num":"2","wx":{"num":"15","free":"1.75","pay_num":"1"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"3","free":"1.75","pay_num":"1","wx":{"num":"3","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"3","free":"0","pay_num":"0","wx":{"num":"3","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-09-03":[{"name":"Win10_Pro_x64_2512.GHO","num":"70","free":"33","pay_num":"11","wx":{"num":"70","free":"33","pay_num":"11"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"91","free":"24","pay_num":"16","wx":{"num":"91","free":"24","pay_num":"16"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"89","free":"12.25","pay_num":"7","wx":{"num":"88","free":"10.5","pay_num":"6"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"37","free":"12","pay_num":"6","wx":{"num":"37","free":"12","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"24","free":"6.25","pay_num":"5","wx":{"num":"24","free":"6.25","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"1.75","pay_num":"1","wx":{"num":"6","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"6","free":"1.75","pay_num":"1","wx":{"num":"6","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607b.WIM","num":"5","free":"0","pay_num":"0","wx":{"num":"5","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-09-02":[{"name":"Win10_Pro_x64_2512.GHO","num":"52","free":"18","pay_num":"6","wx":{"num":"52","free":"18","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"68","free":"12.25","pay_num":"7","wx":{"num":"67","free":"10.5","pay_num":"6"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"90","free":"9","pay_num":"6","wx":{"num":"90","free":"9","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"30","free":"2","pay_num":"1","wx":{"num":"30","free":"2","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"11","free":"1.75","pay_num":"1","wx":{"num":"11","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"5","free":"1","pay_num":"1","wx":{"num":"5","free":"1","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"7","free":"0","pay_num":"0","wx":{"num":"7","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"10","free":"0","pay_num":"0","wx":{"num":"10","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-09-01":[{"name":"Win10_Pro_x64_2512.GHO","num":"77","free":"42","pay_num":"14","wx":{"num":"76","free":"39","pay_num":"13"},"ali":{"num":"1","free":"3","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"109","free":"19.5","pay_num":"13","wx":{"num":"107","free":"16.5","pay_num":"11"},"ali":{"num":"2","free":"3","pay_num":"2"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"36","free":"8","pay_num":"4","wx":{"num":"36","free":"8","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"70","free":"7","pay_num":"4","wx":{"num":"69","free":"5.25","pay_num":"3"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win7_x64_22.3.GHO","num":"10","free":"1.75","pay_num":"1","wx":{"num":"10","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"5","free":"1.75","pay_num":"1","wx":{"num":"5","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"21","free":"1.25","pay_num":"1","wx":{"num":"21","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_0103b.WIM","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607b.WIM","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-31":[{"name":"Win10_Pro_x64_2512.WIM","num":"118","free":"34.5","pay_num":"23","wx":{"num":"113","free":"27","pay_num":"18"},"ali":{"num":"5","free":"7.5","pay_num":"5"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"59","free":"15.75","pay_num":"9","wx":{"num":"59","free":"15.75","pay_num":"9"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.GHO","num":"63","free":"15","pay_num":"5","wx":{"num":"63","free":"15","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"5","free":"3.5","pay_num":"2","wx":{"num":"5","free":"3.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"21","free":"2","pay_num":"1","wx":{"num":"21","free":"2","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"9","free":"1.25","pay_num":"1","wx":{"num":"9","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"0","pay_num":"0","wx":{"num":"6","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607b.WIM","num":"3","free":"0","pay_num":"0","wx":{"num":"3","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-30":[{"name":"Win10_Pro_x64_2512.GHO","num":"71","free":"33","pay_num":"11","wx":{"num":"71","free":"33","pay_num":"11"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"83","free":"15.75","pay_num":"9","wx":{"num":"81","free":"12.25","pay_num":"7"},"ali":{"num":"2","free":"3.5","pay_num":"2"}},{"name":"Win10_Pro_x64_2512.WIM","num":"65","free":"12","pay_num":"8","wx":{"num":"65","free":"12","pay_num":"8"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"22","free":"6","pay_num":"3","wx":{"num":"21","free":"4","pay_num":"2"},"ali":{"num":"1","free":"2","pay_num":"1"}},{"name":"Win10_LTSC_x64_2508.WIM","num":"11","free":"2.5","pay_num":"2","wx":{"num":"11","free":"2.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"6","free":"1.75","pay_num":"1","wx":{"num":"6","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-29":[{"name":"Win10_Pro_x64_2512.GHO","num":"68","free":"21","pay_num":"7","wx":{"num":"68","free":"21","pay_num":"7"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"117","free":"13.5","pay_num":"9","wx":{"num":"116","free":"12","pay_num":"8"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"51","free":"12.25","pay_num":"7","wx":{"num":"51","free":"12.25","pay_num":"7"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"38","free":"4","pay_num":"2","wx":{"num":"38","free":"4","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"23","free":"1.25","pay_num":"1","wx":{"num":"22","free":"0","pay_num":"0"},"ali":{"num":"1","free":"1.25","pay_num":"1"}},{"name":"Win11_LTSC_x64_0103b.WIM","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"0","pay_num":"0","wx":{"num":"6","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-28":[{"name":"Win10_Pro_x64_2512.GHO","num":"69","free":"27","pay_num":"9","wx":{"num":"67","free":"21","pay_num":"7"},"ali":{"num":"2","free":"6","pay_num":"2"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"126","free":"19.25","pay_num":"11","wx":{"num":"126","free":"19.25","pay_num":"11"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"119","free":"18","pay_num":"12","wx":{"num":"118","free":"16.5","pay_num":"11"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"27","free":"6","pay_num":"3","wx":{"num":"27","free":"6","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"6","free":"1.75","pay_num":"1","wx":{"num":"6","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"17","free":"0","pay_num":"0","wx":{"num":"17","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"4","free":"0","pay_num":"0","wx":{"num":"4","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607b.WIM","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-27":[{"name":"Win10_Pro_x64_2512.GHO","num":"75","free":"24","pay_num":"8","wx":{"num":"75","free":"24","pay_num":"8"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"124","free":"19.5","pay_num":"13","wx":{"num":"123","free":"18","pay_num":"12"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"97","free":"17.5","pay_num":"10","wx":{"num":"96","free":"15.75","pay_num":"9"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"32","free":"10","pay_num":"5","wx":{"num":"32","free":"10","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"5","free":"1","pay_num":"1","wx":{"num":"5","free":"1","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"11","free":"0","pay_num":"0","wx":{"num":"11","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"3","free":"0","pay_num":"0","wx":{"num":"3","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-26":[{"name":"Win10_Pro_x64_2512.WIM","num":"107","free":"21","pay_num":"14","wx":{"num":"106","free":"19.5","pay_num":"13"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.GHO","num":"57","free":"15","pay_num":"5","wx":{"num":"56","free":"12","pay_num":"4"},"ali":{"num":"1","free":"3","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"52","free":"10.5","pay_num":"6","wx":{"num":"52","free":"10.5","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"41","free":"6","pay_num":"3","wx":{"num":"41","free":"6","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"12","free":"1.25","pay_num":"1","wx":{"num":"12","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"0","pay_num":"0","wx":{"num":"6","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"3","free":"0","pay_num":"0","wx":{"num":"3","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-25":[{"name":"Win10_Pro_x64_2512.WIM","num":"88","free":"12","pay_num":"8","wx":{"num":"88","free":"12","pay_num":"8"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.GHO","num":"49","free":"12","pay_num":"4","wx":{"num":"49","free":"12","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"75","free":"8.75","pay_num":"5","wx":{"num":"75","free":"8.75","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"24","free":"4","pay_num":"2","wx":{"num":"24","free":"4","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"4","free":"3.5","pay_num":"2","wx":{"num":"4","free":"3.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"18","free":"1.25","pay_num":"1","wx":{"num":"18","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"4","free":"1","pay_num":"1","wx":{"num":"4","free":"1","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"5","free":"0","pay_num":"0","wx":{"num":"5","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-24":[{"name":"Win10_Pro_x64_2512.WIM","num":"91","free":"21","pay_num":"14","wx":{"num":"90","free":"19.5","pay_num":"13"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.GHO","num":"52","free":"18","pay_num":"6","wx":{"num":"52","free":"18","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"58","free":"8.76","pay_num":"6","wx":{"num":"58","free":"8.76","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"9","free":"2.5","pay_num":"2","wx":{"num":"9","free":"2.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"8","free":"0","pay_num":"0","wx":{"num":"8","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"28","free":"0","pay_num":"0","wx":{"num":"28","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-22":[{"name":"Win10_Pro_x64_2512.GHO","num":"61","free":"15","pay_num":"5","wx":{"num":"61","free":"15","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"81","free":"13.5","pay_num":"9","wx":{"num":"79","free":"10.5","pay_num":"7"},"ali":{"num":"2","free":"3","pay_num":"2"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"90","free":"3.5","pay_num":"2","wx":{"num":"90","free":"3.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"26","free":"2","pay_num":"1","wx":{"num":"26","free":"2","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"4","free":"0","pay_num":"0","wx":{"num":"4","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"5","free":"0","pay_num":"0","wx":{"num":"5","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"4","free":"0","pay_num":"0","wx":{"num":"4","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-21":[{"name":"Win10_Pro_x64_2512.GHO","num":"79","free":"24","pay_num":"8","wx":{"num":"78","free":"21","pay_num":"7"},"ali":{"num":"1","free":"3","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"79","free":"13.5","pay_num":"9","wx":{"num":"78","free":"12","pay_num":"8"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"55","free":"12.25","pay_num":"7","wx":{"num":"55","free":"12.25","pay_num":"7"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"5","free":"4","pay_num":"4","wx":{"num":"5","free":"4","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"20","free":"1.25","pay_num":"1","wx":{"num":"20","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"5","free":"0","pay_num":"0","wx":{"num":"5","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"25","free":"0","pay_num":"0","wx":{"num":"25","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-20":[{"name":"Win10_Pro_x64_2512.GHO","num":"60","free":"27","pay_num":"9","wx":{"num":"60","free":"27","pay_num":"9"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"63","free":"10.5","pay_num":"6","wx":{"num":"63","free":"10.5","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"62","free":"7.5","pay_num":"5","wx":{"num":"61","free":"6","pay_num":"4"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"47","free":"4","pay_num":"2","wx":{"num":"47","free":"4","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"10","free":"1.75","pay_num":"1","wx":{"num":"10","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"8","free":"1.75","pay_num":"1","wx":{"num":"8","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"22","free":"1.25","pay_num":"1","wx":{"num":"22","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-19":[{"name":"Win10_Pro_x64_2512.GHO","num":"65","free":"12","pay_num":"4","wx":{"num":"65","free":"12","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"89","free":"12","pay_num":"8","wx":{"num":"88","free":"10.5","pay_num":"7"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"58","free":"10.5","pay_num":"6","wx":{"num":"58","free":"10.5","pay_num":"6"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"31","free":"6","pay_num":"3","wx":{"num":"31","free":"6","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"4","free":"1.75","pay_num":"1","wx":{"num":"4","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"2","free":"1","pay_num":"1","wx":{"num":"2","free":"1","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"8","free":"0","pay_num":"0","wx":{"num":"8","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"3","free":"0","pay_num":"0","wx":{"num":"3","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-18":[{"name":"Win10_Pro_x64_2512.GHO","num":"72","free":"42","pay_num":"14","wx":{"num":"72","free":"42","pay_num":"14"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"113","free":"22.5","pay_num":"15","wx":{"num":"112","free":"21","pay_num":"14"},"ali":{"num":"1","free":"1.5","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"77","free":"10.5","pay_num":"6","wx":{"num":"76","free":"8.75","pay_num":"5"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win10_LTSC_x64_2508.WIM","num":"11","free":"3.75","pay_num":"3","wx":{"num":"11","free":"3.75","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"29","free":"2","pay_num":"1","wx":{"num":"29","free":"2","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"4","free":"1.75","pay_num":"1","wx":{"num":"4","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"4","free":"0","pay_num":"0","wx":{"num":"4","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-17":[{"name":"Win10_Pro_x64_2512.GHO","num":"64","free":"21","pay_num":"7","wx":{"num":"63","free":"18","pay_num":"6"},"ali":{"num":"1","free":"3","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"60","free":"15.75","pay_num":"9","wx":{"num":"59","free":"14","pay_num":"8"},"ali":{"num":"1","free":"1.75","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"87","free":"10.5","pay_num":"7","wx":{"num":"87","free":"10.5","pay_num":"7"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"18","free":"6","pay_num":"3","wx":{"num":"18","free":"6","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"8","free":"1.75","pay_num":"1","wx":{"num":"8","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"12","free":"1.25","pay_num":"1","wx":{"num":"12","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-16":[{"name":"Win10_Pro_x64_2512.WIM","num":"100","free":"21","pay_num":"14","wx":{"num":"100","free":"21","pay_num":"14"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.GHO","num":"44","free":"9","pay_num":"3","wx":{"num":"44","free":"9","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"38","free":"7","pay_num":"4","wx":{"num":"38","free":"7","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"12","free":"2.5","pay_num":"2","wx":{"num":"11","free":"1.25","pay_num":"1"},"ali":{"num":"1","free":"1.25","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"20","free":"2","pay_num":"1","wx":{"num":"20","free":"2","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"2","free":"1.75","pay_num":"1","wx":{"num":"2","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"13","free":"1.75","pay_num":"1","wx":{"num":"13","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-15":[{"name":"Win10_Pro_x64_2512.GHO","num":"75","free":"30","pay_num":"10","wx":{"num":"74","free":"27","pay_num":"9"},"ali":{"num":"1","free":"3","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"96","free":"22.5","pay_num":"15","wx":{"num":"93","free":"18","pay_num":"12"},"ali":{"num":"3","free":"4.5","pay_num":"3"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"95","free":"14","pay_num":"8","wx":{"num":"95","free":"14","pay_num":"8"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"35","free":"10","pay_num":"5","wx":{"num":"34","free":"8","pay_num":"4"},"ali":{"num":"1","free":"2","pay_num":"1"}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"3.5","pay_num":"2","wx":{"num":"6","free":"3.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"8","free":"2.5","pay_num":"2","wx":{"num":"8","free":"2.5","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-14":[{"name":"Win10_Pro_x64_2512.WIM","num":"129","free":"21","pay_num":"14","wx":{"num":"129","free":"21","pay_num":"14"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"85","free":"21","pay_num":"12","wx":{"num":"83","free":"17.5","pay_num":"10"},"ali":{"num":"2","free":"3.5","pay_num":"2"}},{"name":"Win10_Pro_x64_2512.GHO","num":"70","free":"21","pay_num":"7","wx":{"num":"70","free":"21","pay_num":"7"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"31","free":"8","pay_num":"4","wx":{"num":"31","free":"8","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"5","free":"1.75","pay_num":"1","wx":{"num":"5","free":"1.75","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"21","free":"1.25","pay_num":"1","wx":{"num":"21","free":"1.25","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"4","free":"0","pay_num":"0","wx":{"num":"4","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-13":[{"name":"Win10_Pro_x64_2512.WIM","num":"103","free":"15.3","pay_num":"11","wx":{"num":"102","free":"14.1","pay_num":"10"},"ali":{"num":"1","free":"1.2","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.GHO","num":"92","free":"15","pay_num":"6","wx":{"num":"91","free":"12.6","pay_num":"5"},"ali":{"num":"1","free":"2.4","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"69","free":"11.55","pay_num":"8","wx":{"num":"69","free":"11.55","pay_num":"8"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"33","free":"3.2","pay_num":"2","wx":{"num":"33","free":"3.2","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"12","free":"0","pay_num":"0","wx":{"num":"12","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"4","free":"0","pay_num":"0","wx":{"num":"4","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-12":[{"name":"Win10_Pro_x64_2512.WIM","num":"71","free":"18","pay_num":"15","wx":{"num":"71","free":"18","pay_num":"15"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"41","free":"9.8","pay_num":"7","wx":{"num":"41","free":"9.8","pay_num":"7"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.GHO","num":"47","free":"7.2","pay_num":"3","wx":{"num":"46","free":"4.8","pay_num":"2"},"ali":{"num":"1","free":"2.4","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"39","free":"4.8","pay_num":"3","wx":{"num":"39","free":"4.8","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"11","free":"2","pay_num":"2","wx":{"num":"11","free":"2","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"8","free":"1.4","pay_num":"1","wx":{"num":"8","free":"1.4","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"1","free":"0","pay_num":"0","wx":{"num":"1","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"7","free":"0","pay_num":"0","wx":{"num":"7","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-11":[{"name":"Win10_Pro_x64_2512.GHO","num":"55","free":"26.4","pay_num":"11","wx":{"num":"54","free":"24","pay_num":"10"},"ali":{"num":"1","free":"2.4","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"80","free":"16.8","pay_num":"12","wx":{"num":"77","free":"12.6","pay_num":"9"},"ali":{"num":"3","free":"4.2","pay_num":"3"}},{"name":"Win10_Pro_x64_2512.WIM","num":"98","free":"14.4","pay_num":"12","wx":{"num":"98","free":"14.4","pay_num":"12"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"23","free":"6.4","pay_num":"4","wx":{"num":"22","free":"4.8","pay_num":"3"},"ali":{"num":"1","free":"1.6","pay_num":"1"}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"2.8","pay_num":"2","wx":{"num":"6","free":"2.8","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"16","free":"2","pay_num":"2","wx":{"num":"16","free":"2","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"2","free":"1.4","pay_num":"1","wx":{"num":"2","free":"1.4","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x86_22.3.GHO","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-10":[{"name":"Win10_Pro_x64_2512.WIM","num":"93","free":"13.2","pay_num":"11","wx":{"num":"92","free":"12","pay_num":"10"},"ali":{"num":"1","free":"1.2","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.GHO","num":"61","free":"9.6","pay_num":"4","wx":{"num":"60","free":"7.2","pay_num":"3"},"ali":{"num":"1","free":"2.4","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"24","free":"8","pay_num":"5","wx":{"num":"24","free":"8","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"59","free":"7","pay_num":"5","wx":{"num":"58","free":"5.6","pay_num":"4"},"ali":{"num":"1","free":"1.4","pay_num":"1"}},{"name":"Win10_LTSC_x64_2508.WIM","num":"15","free":"1","pay_num":"1","wx":{"num":"14","free":"0","pay_num":"0"},"ali":{"num":"1","free":"1","pay_num":"1"}},{"name":"Win7_x64_22.3.GHO","num":"8","free":"0","pay_num":"0","wx":{"num":"8","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"6","free":"0","pay_num":"0","wx":{"num":"6","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-09":[{"name":"Win10_Pro_x64_2512.GHO","num":"92","free":"43.2","pay_num":"18","wx":{"num":"90","free":"38.4","pay_num":"16"},"ali":{"num":"2","free":"4.8","pay_num":"2"}},{"name":"Win10_Pro_x64_2512.WIM","num":"72","free":"15.6","pay_num":"13","wx":{"num":"70","free":"13.2","pay_num":"11"},"ali":{"num":"2","free":"2.4","pay_num":"2"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"56","free":"15.4","pay_num":"11","wx":{"num":"56","free":"15.4","pay_num":"11"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"20","free":"3.2","pay_num":"2","wx":{"num":"20","free":"3.2","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"8","free":"2.8","pay_num":"2","wx":{"num":"8","free":"2.8","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"6","free":"1.4","pay_num":"1","wx":{"num":"5","free":"0","pay_num":"0"},"ali":{"num":"1","free":"1.4","pay_num":"1"}},{"name":"Win10_LTSC_x64_2508.WIM","num":"16","free":"0","pay_num":"0","wx":{"num":"16","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-08":[{"name":"Win10_Pro_x64_2512.GHO","num":"68","free":"26.4","pay_num":"11","wx":{"num":"66","free":"21.6","pay_num":"9"},"ali":{"num":"2","free":"4.8","pay_num":"2"}},{"name":"Win10_Pro_x64_2512.WIM","num":"118","free":"20.4","pay_num":"17","wx":{"num":"116","free":"18","pay_num":"15"},"ali":{"num":"2","free":"2.4","pay_num":"2"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"59","free":"11.2","pay_num":"8","wx":{"num":"58","free":"9.8","pay_num":"7"},"ali":{"num":"1","free":"1.4","pay_num":"1"}},{"name":"Win10_Pro_x64_2512b.WIM","num":"16","free":"4.8","pay_num":"3","wx":{"num":"16","free":"4.8","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"13","free":"2","pay_num":"2","wx":{"num":"13","free":"2","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"2","free":"0","pay_num":"0","wx":{"num":"2","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-07":[{"name":"Win10_Pro_x64_2512.GHO","num":"57","free":"12","pay_num":"5","wx":{"num":"57","free":"12","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512.WIM","num":"102","free":"10.8","pay_num":"9","wx":{"num":"102","free":"10.8","pay_num":"9"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"29","free":"6.4","pay_num":"4","wx":{"num":"29","free":"6.4","pay_num":"4"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win11_LTSC_x64_2607.WIM","num":"56","free":"4.2","pay_num":"3","wx":{"num":"56","free":"4.2","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"8","free":"1.4","pay_num":"1","wx":{"num":"8","free":"1.4","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"7","free":"1","pay_num":"1","wx":{"num":"7","free":"1","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"5","free":"0","pay_num":"0","wx":{"num":"5","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}],"2026-08-06":[{"name":"Win10_Pro_x64_2512.GHO","num":"59","free":"19.2","pay_num":"8","wx":{"num":"58","free":"16.8","pay_num":"7"},"ali":{"num":"1","free":"2.4","pay_num":"1"}},{"name":"Win10_Pro_x64_2512.WIM","num":"80","free":"15.6","pay_num":"13","wx":{"num":"79","free":"14.4","pay_num":"12"},"ali":{"num":"1","free":"1.2","pay_num":"1"}},{"name":"Win11_LTSC_x64_2607.WIM","num":"41","free":"7","pay_num":"5","wx":{"num":"41","free":"7","pay_num":"5"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_Pro_x64_2512b.WIM","num":"31","free":"4.8","pay_num":"3","wx":{"num":"31","free":"4.8","pay_num":"3"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win7_x64_22.3.GHO","num":"6","free":"2.8","pay_num":"2","wx":{"num":"6","free":"2.8","pay_num":"2"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508.WIM","num":"12","free":"2","pay_num":"2","wx":{"num":"11","free":"1","pay_num":"1"},"ali":{"num":"1","free":"1","pay_num":"1"}},{"name":"Win7_x86_22.3.GHO","num":"2","free":"0.8","pay_num":"1","wx":{"num":"2","free":"0.8","pay_num":"1"},"ali":{"num":0,"free":0,"pay_num":0}},{"name":"Win10_LTSC_x64_2508b.WIM","num":"3","free":"0","pay_num":"0","wx":{"num":"3","free":"0","pay_num":"0"},"ali":{"num":0,"free":0,"pay_num":0}}]};

const API_BASE = 'https://pay.iwin11.cn/api/statistics';
const DEFAULT_CRED = {
  key: '5ca53a4f5d5ae45b364c5064db75087b',
  time: '1788660003'
};

function utilToNumber(str) {
  const n = Number(str);
  return Number.isNaN(n) ? 0 : n;
}

function utilFormatMoney(value) {
  const n = typeof value === 'number' ? value : utilToNumber(value);
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
}

function utilCalcRate(payNum, orderNum) {
  const p = utilToNumber(payNum);
  const o = utilToNumber(orderNum);
  if (o <= 0) return null;
  const rate = p / o * 100;
  return {
    text: rate.toFixed(1) + '%',
    level: rate >= 20 ? 'high' : (rate >= 10 ? 'mid' : 'low')
  };
}

function utilNumOf(obj, key) {
  return Number((obj && obj[key] != null) ? obj[key] : 0);
}

function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

const METRICS = {
  free:     { label: '支付金额', unit: '', colorVar: '--color-primary', format: utilFormatMoney },
  num:      { label: '订单数',   unit: '', colorVar: '--color-green',   format: function (v) { return v; } },
  pay_num:  { label: '支付笔数', unit: '', colorVar: '--color-amber',   format: function (v) { return v; } }
};
Object.values(METRICS).forEach(function (m) { m.color = cssVar(m.colorVar) || '#5691f7'; });

const KPI_ORDER = [
  { key: 'free', label: '支付金额' },
  { key: 'pay_num', label: '支付笔数' },
  { key: 'num', label: '订单数' }
];

const METRIC_TABS = [
  { key: 'free', label: '支付金额' },
  { key: 'num', label: '订单数' },
  { key: 'pay_num', label: '支付笔数' }
];

let chartInstance = null;
const pendingDetails = new Set();


export default {
  name: 'StatisticsPage',
  data: function () {
    return {
      apiData: FALLBACK,
      isLiveData: false,
      expandedDate: null,
      sortKey: 'date',
      sortDir: 'desc',
      currentMetric: 'free',
      isRefreshing: false,
      statusType: 'live',
      statusText: '离线快照 · 数据已内嵌',
      detailState: {},
      dayDetailCache: {},
      metricTabs: METRIC_TABS,
      cred: { key: DEFAULT_CRED.key, time: DEFAULT_CRED.time }
    };
  },
  computed: {
    kpiCards: function () {
      var self = this;
      var today = this.apiData.today;
      var total = this.apiData.total;
      return KPI_ORDER.map(function (item) {
        var cfg = METRICS[item.key];
        var todayVal = self.toNumber(today[item.key]);
        var totalVal = self.toNumber(total[item.key]);
        return {
          key: item.key,
          label: item.label,
          todayText: cfg.format(todayVal),
          totalText: cfg.format(totalVal)
        };
      });
    },
    sortedList: function () {
      var self = this;
      var dir = this.sortDir === 'asc' ? 1 : -1;
      return this.apiData.list.slice().sort(function (a, b) {
        var av = self.sortValue(a, self.sortKey);
        var bv = self.sortValue(b, self.sortKey);
        if (typeof av === 'string' || typeof bv === 'string') {
          return String(av).localeCompare(String(bv)) * dir;
        }
        return (av - bv) * dir;
      });
    }
  },
  methods: {
    toNumber: utilToNumber,
    formatMoney: utilFormatMoney,
    calcRate: utilCalcRate,
    numOf: utilNumOf,
    sortValue: function (row, key) {
      if (key === 'rate') {
        var o = this.toNumber(row.num);
        return o > 0 ? this.toNumber(row.pay_num) / o : -1;
      }
      if (key === 'date') return row.date;
      return this.toNumber(row[key]);
    },
    rateText: function (row) {
      var r = this.calcRate(row.pay_num, row.num);
      return r ? r.text : '-';
    },
    rateCellClass: function (row) {
      var r = this.calcRate(row.pay_num, row.num);
      return r ? 'daily-row__rate--' + r.level : '';
    },
    rateLevel: function (item) {
      var r = this.calcRate(item.pay_num, item.num);
      return r ? r.level : 'low';
    },
    sortClass: function (key) {
      var active = this.sortKey === key;
      return {
        'is-sorted': active,
        'is-sorted-asc': active && this.sortDir === 'asc',
        'is-sorted-desc': active && this.sortDir === 'desc'
      };
    },
    ariaSort: function (key) {
      if (this.sortKey !== key) return 'none';
      return this.sortDir === 'asc' ? 'ascending' : 'descending';
    },
    applySort: function (key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'desc';
      }
    },
    toggleRow: function (date) {
      if (this.expandedDate === date) {
        this.expandedDate = null;
      } else {
        this.expandedDate = date;
        this.loadDayDetail(date);
      }
    },
    switchChart: function (metricKey) {
      if (metricKey === this.currentMetric) return;
      this.currentMetric = metricKey;
      if (chartInstance) chartInstance.setOption(this.getChartOption(metricKey), true);
    },
    initDetailState: function () {
      var ds = {};
      this.apiData.list.forEach(function (d) {
        ds[d.date] = { status: 'idle', items: [], error: '' };
      });
      this.detailState = ds;
    },
    getChartOption: function (metricKey) {
      var cfg = METRICS[metricKey];
      var color = cssVar(cfg.colorVar) || cfg.color;
      var areaAlpha = cssVar('--chart-area-alpha') || '40';
      var sorted = this.apiData.list.slice().sort(function (a, b) {
        return new Date(a.date) - new Date(b.date);
      });
      var dates = sorted.map(function (r) { return r.date.slice(5); });
      var values = sorted.map(function (r) { return utilToNumber(r[metricKey]); });
      return {
        tooltip: {
          trigger: 'axis',
          backgroundColor: cssVar('--chart-tooltip-bg'),
          borderColor: cssVar('--chart-tooltip-border'),
          textStyle: { color: cssVar('--chart-tooltip-text'), fontSize: 13 },
          formatter: function (params) {
            var p = params[0];
            return '<div style="font-weight:600;margin-bottom:4px">2026-' + p.name + '</div>' + cfg.label + '：' + cfg.format(p.value) + ' ' + cfg.unit;
          }
        },
        grid: { left: '1%', right: '1%', bottom: '6%', top: '10%', containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLine: { lineStyle: { color: cssVar('--chart-axis') } },
          axisLabel: { color: cssVar('--chart-label'), fontSize: 12 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: cssVar('--chart-split'), type: 'dashed' } },
          axisLabel: { color: cssVar('--chart-label-dim'), fontSize: 12 }
        },
        series: [{
          name: cfg.label,
          type: 'line',
          smooth: true,
          symbol: 'circle',
          symbolSize: 7,
          itemStyle: { color: color, borderWidth: 2, borderColor: cssVar('--chart-symbol-border') },
          lineStyle: { width: 3, color: color },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: color + areaAlpha },
                { offset: 1, color: color + '05' }
              ]
            }
          },
          data: values,
          animationDuration: 800
        }]
      };
    },
    initChart: function () {
      if (typeof echarts === 'undefined') {
        if (this.$refs.chart) this.$refs.chart.innerHTML = '<div class="status-message">图表库加载失败，请检查网络连接。</div>';
        return;
      }
      chartInstance = echarts.init(this.$refs.chart);
      chartInstance.setOption(this.getChartOption(this.currentMetric));
      window.addEventListener('resize', function () {
        if (chartInstance) chartInstance.resize();
      });
    },
    loadDayDetail: function (date) {
      var self = this;
      if (!this.detailState[date]) {
        this.$set(this.detailState, date, { status: 'idle', items: [], error: '' });
      }
      var st = this.detailState[date];
      if (st.status === 'loaded' || st.status === 'loading') return;
      if (this.dayDetailCache[date]) {
        this.$set(this.detailState, date, { status: 'loaded', items: this.dayDetailCache[date], error: '' });
        return;
      }
      if (pendingDetails.has(date)) return;
      pendingDetails.add(date);
      this.$set(this.detailState, date, { status: 'loading', items: [], error: '' });
      this.fetchDayDetail(date).then(function (items) {
        self.$set(self.detailState, date, { status: 'loaded', items: items || [], error: '' });
      }).catch(function (err) {
        self.$set(self.detailState, date, { status: 'error', items: [], error: (err && err.message) || '网络异常' });
      }).then(function () {
        pendingDetails.delete(date);
      });
    },
    fetchDayDetail: function (date) {
      if (this.dayDetailCache[date]) return Promise.resolve(this.dayDetailCache[date]);
      var self = this;
      return this.requestJson('day', { date: date }).then(function (data) {
        self.dayDetailCache[date] = data || [];
        return self.dayDetailCache[date];
      });
    },
    fetchThirtyDays: function () {
      return this.requestJson('thirty_days');
    },
    requestJson: function (path, extra) {
      var url = this.buildUrl(path, extra);
      return fetch(url, { method: 'GET', headers: { 'Accept': 'application/json' }, cache: 'no-store' })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        }).then(function (json) {
          if (json.code !== 200) throw new Error(json.message || '接口返回异常');
          return json.data;
        });
    },
    buildUrl: function (path, extra) {
      var params = new URLSearchParams({ key: this.cred.key, time: this.cred.time });
      if (extra) Object.keys(extra).forEach(function (k) { params.append(k, extra[k]); });
      return API_BASE + '/' + path + '?' + params.toString();
    },
    readCredentials: function () {
      var params = new URLSearchParams(window.location.search);
      return {
        key: params.get('key') || DEFAULT_CRED.key,
        time: params.get('time') || DEFAULT_CRED.time
      };
    },
    setStatus: function (type, text) {
      this.statusType = type;
      this.statusText = text;
    },
    refreshData: function () {
      var self = this;
      this.isRefreshing = true;
      this.setStatus('loading', '正在同步实时数据…');
      this.fetchThirtyDays().then(function (data) {
        if (!data || !Array.isArray(data.list) || data.list.length === 0) {
          throw new Error('接口未返回有效数据');
        }
        self.expandedDate = null;
        self.dayDetailCache = {};
        self.apiData = data;
        self.isLiveData = true;
        self.initDetailState();
        var now = new Date();
        var stamp = [now.getHours(), now.getMinutes(), now.getSeconds()]
          .map(function (n) { return String(n).padStart(2, '0'); }).join(':');
        self.setStatus('live', '实时数据 · ' + self.apiData.today.date + ' · 同步于 ' + stamp);
      }).catch(function (err) {
        console.error('[statistics] 同步实时数据失败', err);
        self.setStatus('error', '实时数据同步失败（' + ((err && err.message) || '网络异常') + '），当前为离线快照');
      }).then(function () {
        self.isRefreshing = false;
      });
    },
    syncChartWithTheme: function () {
      Object.keys(METRICS).forEach(function (k) { METRICS[k].color = cssVar(METRICS[k].colorVar) || METRICS[k].color; });
      if (chartInstance) chartInstance.setOption(this.getChartOption(this.currentMetric), true);
    },
    bindThemeChange: function () {
      var self = this;
      var darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
      var handler = function () { self.syncChartWithTheme(); };
      if (darkQuery.addEventListener) darkQuery.addEventListener('change', handler);
      else if (darkQuery.addListener) darkQuery.addListener(handler);
    }
  },
  watch: {
    currentMetric: function () {
      if (chartInstance) chartInstance.setOption(this.getChartOption(this.currentMetric), true);
    },
    apiData: {
      deep: true,
      handler: function () {
        if (chartInstance) chartInstance.setOption(this.getChartOption(this.currentMetric), true);
      }
    }
  },
  created: function () {
    this.cred = this.readCredentials();
    this.initDetailState();
    this.dayDetailCache = JSON.parse(JSON.stringify(DAY_DETAILS));
    Object.keys(METRICS).forEach(function (k) { METRICS[k].color = cssVar(METRICS[k].colorVar) || METRICS[k].color; });
  },
  mounted: function () {
    this.initChart();
    this.bindThemeChange();
    this.refreshData();
  },
  beforeDestroy: function () {
    if (chartInstance) { chartInstance.dispose(); chartInstance = null; }
  }

};
</script>

<style>

:root {
  /* 品牌与基础色 */
  --color-primary: #5691f7;
  --color-primary-dark: #3b78e0;
  --color-primary-light: #e8f0fe;
  --color-bg: #f8f8fa;
  --color-surface: #ffffff;
  --color-text: #333333;
  --color-text-secondary: #666666;
  --color-text-muted: #999999;
  --color-border: #eeeeee;
  --color-green: #34c759;
  --color-amber: #f59e0b;
  --color-red: #ef4444;

  /* 尺寸与效果 */
  --radius: 12px;
  --shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  --shadow-hover: 0 8px 24px rgba(86, 145, 247, 0.18);
  --max-width: 960px;
  --space: 16px;

  /* 表面层次与边框 */
  --color-surface-alt: #fafafa;
  --color-chip-bg: #f2f4f7;
  --color-border-strong: #d0d7de;

  /* 交互态 */
  --color-row-hover: #f6faff;
  --color-row-hover-strong: #f0f7ff;
  --color-thead-hover: #f2f6fc;
  --color-tab-hover: #c8d8fa;
  --color-btn-hover: #dbe9ff;

  /* 反色文字（彩色 / 深色底上的文字） */
  --color-text-inverse: #ffffff;

  /* 状态语义：浅底 + 深字 */
  --color-success: #12a150;
  --color-success-bg: #e8f8ee;
  --color-error: #d4380d;
  --color-error-bg: #fff1f0;
  --color-info: #4a7cf7;
  --color-info-bg: #eef4ff;

  /* 转化率档位色 */
  --color-rate-high: #12a150;
  --color-rate-mid: var(--color-primary);
  --color-rate-low: #d48806;

  /* KPI 卡片渐变与装饰：独立于品牌色，便于深浅色分别调校 */
  --kpi-gradient-1: linear-gradient(135deg, #5691f7 0%, #3b78e0 100%);
  --kpi-gradient-2: linear-gradient(135deg, #3b78e0 0%, #2b5fc7 100%);
  --kpi-gradient-3: linear-gradient(135deg, #5d9aff 0%, #437ee6 100%);
  --kpi-ornament: rgba(255, 255, 255, 0.08);
  --kpi-ornament-sm: rgba(255, 255, 255, 0.05);
  --shadow-primary: 0 2px 8px rgba(86, 145, 247, 0.35);

  /* 图表配色：canvas 无法读 CSS 变量，由 JS 取值后传入 ECharts */
  --chart-axis: #dddddd;
  --chart-label: #666666;
  --chart-label-dim: #888888;
  --chart-split: #f0f0f0;
  --chart-tooltip-bg: rgba(255, 255, 255, 0.96);
  --chart-tooltip-text: #333333;
  --chart-tooltip-border: #eeeeee;
  --chart-symbol-border: #ffffff;
  --chart-area-alpha: 40;
}

/* ===== 深色模式：跟随系统偏好，仅覆盖变量值，不改动任何规则 ===== */
@media (prefers-color-scheme: dark) {
  :root {
    /* 品牌色在深底上提亮一档，保证对比度 */
    --color-primary: #6ea8ff;
    --color-primary-dark: #4d86e8;
    --color-primary-light: #1c2b45;

    /* 表面层次：由深到浅 */
    --color-bg: #12141a;
    --color-surface: #1a1d26;
    --color-surface-alt: #20242f;
    --color-chip-bg: #262b36;

    /* 文字 */
    --color-text: #e8eaed;
    --color-text-secondary: #b0b6c0;
    --color-text-muted: #868d99;

    /* 边框与分割线 */
    --color-border: #2a2f3a;
    --color-border-strong: #3a4150;

    /* 图表系列色同步提亮 */
    --color-green: #4ade80;
    --color-amber: #fbbf24;
    --color-red: #ff6b6b;

    /* 交互态：深底上叠加浅色 */
    --color-row-hover: #232936;
    --color-row-hover-strong: #1e2739;
    --color-thead-hover: #262d3d;
    --color-tab-hover: #2a3b5a;
    --color-btn-hover: #2a3348;

    /* 状态语义：改为半透明彩底 + 亮字，避免深色下出现"发光块" */
    --color-success: #4ade80;
    --color-success-bg: rgba(74, 222, 128, 0.15);
    --color-error: #ff7b7b;
    --color-error-bg: rgba(255, 123, 123, 0.15);
    --color-info: #8ab4ff;
    --color-info-bg: rgba(138, 180, 255, 0.15);

    /* 转化率档位 */
    --color-rate-high: #4ade80;
    --color-rate-mid: var(--color-primary);
    --color-rate-low: #fbbf24;

    /* KPI 渐变整体压暗，保证白字对比度 */
    --kpi-gradient-1: linear-gradient(135deg, #3f6fbf 0%, #2f5599 100%);
    --kpi-gradient-2: linear-gradient(135deg, #2f5599 0%, #24417a 100%);
    --kpi-gradient-3: linear-gradient(135deg, #4a7ac8 0%, #37609f 100%);
    --kpi-ornament: rgba(255, 255, 255, 0.10);
    --kpi-ornament-sm: rgba(255, 255, 255, 0.06);

    /* 阴影在深色下需加重才可见 */
    --shadow: 0 4px 16px rgba(0, 0, 0, 0.45);
    --shadow-hover: 0 8px 24px rgba(110, 168, 255, 0.28);
    --shadow-primary: 0 2px 8px rgba(110, 168, 255, 0.35);

    /* 图表 */
    --chart-axis: #3a4150;
    --chart-label: #9aa3b2;
    --chart-label-dim: #79828f;
    --chart-split: #2a2f3a;
    --chart-tooltip-bg: rgba(30, 34, 44, 0.96);
    --chart-tooltip-text: #e8eaed;
    --chart-tooltip-border: #3a4150;
    --chart-symbol-border: #1a1d26;
    --chart-area-alpha: 33;
  }
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
  "Helvetica Neue", Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
  background: var(--color-bg) !important;
  color: var(--color-text) !important;
  line-height: 1.5 !important;
  -webkit-font-smoothing: antialiased;
}

.statistics-page {
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 24px var(--space);
}

/* 页面标题 */
.page-header {
  margin-bottom: 20px;
}
.page-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.page-header__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text);
}
/* 刷新按钮 */
.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-family: inherit;
  color: var(--color-primary);
  background: var(--color-primary-light);
  border: 1px solid transparent;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.18s ease, transform 0.18s ease, opacity 0.18s ease;
}
.refresh-btn:hover:not(:disabled) {
  background: var(--color-btn-hover);
}
.refresh-btn:active:not(:disabled) {
  transform: scale(0.96);
}
.refresh-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.refresh-btn__icon {
  width: 16px;
  height: 16px;
  display: inline-block;
  color: var(--color-primary);
  transition: transform 0.4s ease;
}
.refresh-btn:active:not(:disabled) .refresh-btn__icon {
  transform: rotate(180deg);
}
/* 拉取数据时按钮进入禁用态，图标持续旋转作为加载指示 */
.refresh-btn:disabled .refresh-btn__icon {
  animation: refresh-spin 0.8s linear infinite;
}
@keyframes refresh-spin {
  to { transform: rotate(360deg); }
}

/* 数据状态条 */
.data-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 999px;
  background: var(--color-chip-bg);
  color: var(--color-text-muted);
}
.data-status__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex: none;
}
.data-status--loading {
  background: var(--color-info-bg);
  color: var(--color-info);
}
.data-status--loading .data-status__dot {
  animation: pulse 1.2s ease-in-out infinite;
}
.data-status--live {
  background: var(--color-success-bg);
  color: var(--color-success);
}
.data-status--error {
  background: var(--color-error-bg);
  color: var(--color-error);
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
}

/* 明细加载 / 错误态 */
.detail-loading,
.detail-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: var(--color-text-muted);
  padding: 12px 0;
}
.detail-error {
  color: var(--color-error);
  flex-wrap: wrap;
}
.spinner {
  width: 13px;
  height: 13px;
  border: 2px solid var(--color-border-strong);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.detail-retry {
  padding: 3px 10px;
  font-size: 12px;
  font-family: inherit;
  color: var(--color-text-inverse);
  background: var(--color-error);
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.18s ease;
}
.detail-retry:hover {
  opacity: 0.85;
}

/* KPI 卡片区 */
.kpi-section {
  display: grid;
  /* minmax(0,1fr) 防止长数字把列撑破，保证任何屏宽下都稳定 3 列 */
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space);
  margin-bottom: var(--space);
}
.kpi-card {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius);
  padding: 24px;
  color: var(--color-text-inverse);
  background: var(--kpi-gradient-1);
  box-shadow: var(--shadow);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.kpi-card:nth-child(2) {
  background: var(--kpi-gradient-2);
}
.kpi-card:nth-child(3) {
  background: var(--kpi-gradient-3);
}
.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-hover);
}
.kpi-card::after {
  content: "";
  position: absolute;
  right: -24px;
  bottom: -24px;
  width: 110px;
  height: 110px;
  background: var(--kpi-ornament);
  border-radius: 50%;
}

.kpi-card__label {
  font-size: 14px;
  opacity: 0.92;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}
.kpi-card__value {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  position: relative;
  z-index: 1;
}
.kpi-card__summary {
  margin-top: 8px;
  font-size: 13px;
  opacity: 0.85;
  position: relative;
  z-index: 1;
}

/* 移动端（含 iPhone X 375px）：保持一行三张卡，突出核心数值 */
@media (max-width: 640px) {
  .kpi-section { gap: 8px; }
  .kpi-card {
    padding: 14px 8px;
    border-radius: 12px;
  }
  .kpi-card::after {
    right: -20px;
    bottom: -20px;
    width: 56px;
    height: 56px;
    background: var(--kpi-ornament-sm);
  }
  .kpi-card__label {
    font-size: 11px;
    margin-bottom: 4px;
    opacity: 0.8;
    white-space: nowrap;
    text-align: center;
  }
  .kpi-card__value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.15;
    white-space: nowrap;
    text-align: center;
  }
  .kpi-card__summary {
    margin-top: 6px;
    font-size: 10px;
    line-height: 1.5;
    opacity: 0.7;
    text-align: center;
  }
  /* 窄屏下"近 30 天"与数值各占一行，且数值行不折行 */
  .kpi-card__summary-prefix {
    display: block;
    opacity: 0.9;
  }
  .kpi-card__summary-value {
    display: block;
    white-space: nowrap;
  }
}


/* 通用卡片 */
.card {
  background: var(--color-surface);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  margin-bottom: var(--space);
  overflow: hidden;
}
.card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px 20px;
}
.card__title {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text);
}

/* 图表切换按钮 */
.chart-tabs {
  display: inline-flex;
  gap: 4px;
  background: var(--color-primary-light);
  border-radius: 999px;
  padding: 4px;
}
.chart-tabs__btn {
  border: none;
  background: transparent;
  color: var(--color-primary-dark);
  font-size: 13px;
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
}
@media (hover: hover) {
  .chart-tabs__btn:hover {
    background: var(--color-tab-hover);
    color: var(--color-primary-dark);
  }
}
.chart-tabs__btn--active {
  background: var(--color-primary);
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-primary);
}

/* 图表容器 */
.chart-container {
  width: 100%;
  height: 320px;
  padding: 8px 12px 12px;
}
@media (max-width: 640px) {
  .chart-container { height: 260px; }
}

/* 数据表格 */
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.data-table thead {
  background: var(--color-surface-alt);
}
.data-table th,
.data-table td {
  padding: 14px 20px;
  text-align: right;
  border-bottom: 1px solid var(--color-border);
}
.data-table th:first-child,
.data-table td:first-child {
  text-align: left;
}
.data-table th {
  color: var(--color-text-secondary);
  font-weight: 600;
  font-size: 13px;
}
.data-table tbody tr {
  transition: background-color 0.15s ease;
}
.data-table tbody tr:hover {
  background-color: var(--color-row-hover);
}
/* 每个日期行后都跟随一个隐藏的详情行，因此视觉上的最后一行是倒数第二个 tr */
.data-table tbody tr:last-child td,
.data-table tbody tr:nth-last-child(2) td {
  border-bottom: none;
}
.data-table td:first-child {
  font-weight: 500;
}
/* 简短表头：默认隐藏，移动端启用 */
.th-short { display: none; }

@media (max-width: 640px) {
  .data-table th,
  .data-table td { padding: 10px 6px; font-size: 12px; }
  /* 首列左侧、末列右侧保留呼吸感 */
  .data-table th:first-child,
  .data-table td:first-child { padding-left: 10px; }
  .data-table th:last-child,
  .data-table td:last-child { padding-right: 10px; }
  /* 主行单元格一律不换行，杜绝日期/数字断行 */
  .daily-row td { white-space: nowrap; }
  /* 表头切简短文案 */
  .th-full { display: none; }
  .th-short { display: inline; }
  /* 日期只保留「月-日」，年份在页面标题处体现 */
  .date-year { display: none; }
}

/* 表头排序：点击切换正序 / 倒序 */
.data-table th[data-sort] {
  cursor: pointer;
  user-select: none;
  -webkit-user-select: none;
  white-space: nowrap;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.data-table th[data-sort]:hover {
  color: var(--color-primary);
  background-color: var(--color-thead-hover);
}
/* 排序指示：纯 CSS 三角形（上下各一），当前排序方向高亮 */
.sort-indicator {
  position: relative;
  display: inline-block;
  width: 8px;
  height: 12px;
  margin-left: 5px;
  vertical-align: middle;
  flex: none;
}
.sort-indicator::before,
.sort-indicator::after {
  content: "";
  position: absolute;
  left: 0;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  opacity: 0.35;
  transition: opacity 0.15s ease;
}
/* 上三角 → 正序 */
.sort-indicator::before {
  top: 0;
  border-bottom: 5px solid currentColor;
}
/* 下三角 → 倒序 */
.sort-indicator::after {
  bottom: 0;
  border-top: 5px solid currentColor;
}
/* 未排序列悬停：箭头加深，提示可点击 */
.data-table th[data-sort]:not(.is-sorted):hover .sort-indicator::before,
.data-table th[data-sort]:not(.is-sorted):hover .sort-indicator::after {
  opacity: 0.65;
}
.data-table th.is-sorted { color: var(--color-primary); }
/* 已排序：非当前方向淡出，当前方向实心高亮 */
.data-table th.is-sorted .sort-indicator::before,
.data-table th.is-sorted .sort-indicator::after { opacity: 0.25; }
.data-table th.is-sorted-asc .sort-indicator::before { opacity: 1; }
.data-table th.is-sorted-desc .sort-indicator::after { opacity: 1; }

/* 支付转化率：按区间着色，便于快速识别 */
.daily-row__rate {
  font-variant-numeric: tabular-nums;
}
.daily-row__rate--high {
  color: var(--color-rate-high);
  font-weight: 600;
}
.daily-row__rate--mid {
  color: var(--color-primary);
  font-weight: 600;
}
.daily-row__rate--low {
  color: var(--color-rate-low);
  font-weight: 600;
}

/* 页脚提示 */
.page-footer {
  text-align: center;
  font-size: 12px;
  color: var(--color-text-muted);
  padding: 8px 0 24px;
}

/* 加载/错误提示 */
.status-message {
  padding: 40px;
  text-align: center;
  color: var(--color-text-secondary);
}

/* 数值进入动画 */
@keyframes countUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.kpi-card__value,
.data-table td {
  animation: countUp 0.4s ease both;
}

/* 每日行可展开 */
.daily-row {
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.daily-row:hover {
  background-color: var(--color-row-hover-strong);
}
/* 注意：<td> 上不能改 display。改成 flex 会让它脱离 table-cell 布局，
   该列的下边框就不参与 border-collapse 绘制，导致整列下边框断线、与其它列对不齐。
   这里 td 保持表格单元格，flex 对齐交给内层容器。 */
.daily-row__date {
  font-weight: 500;
}
.daily-row__date-inner {
  display: flex;
  align-items: center;
  gap: 8px;
}
.expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 18px;
  line-height: 1;
  color: var(--color-text-muted);
  transition: transform 0.2s ease, color 0.2s ease;
}
.expand-icon::before {
  content: '›';
  transform: translateX(-1px);
}
.daily-row:hover .expand-icon {
  color: var(--color-primary);
}
.daily-row--expanded .expand-icon {
  transform: rotate(90deg);
  color: var(--color-primary);
}

/* 详情行 */
.detail-row {
}

.detail-row > td {
  padding: 0 12px 12px;
  border-bottom: none;
}
@media (max-width: 640px) {
  .detail-row > td { padding: 0 8px 10px; }
}

.detail-panel {
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  animation: fadeIn 0.25s ease;
}
.detail-empty {
  font-size: 13px;
  color: var(--color-text-muted);
  text-align: center;
  padding: 16px 0;
}

.detail-list {
  display: flex;
  flex-direction: column;
}
.detail-item {
  padding: 14px 16px;
  border-bottom: 1px solid var(--color-border);
}
.detail-item:last-child {
  border-bottom: none;
}
.detail-item__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 12px;
  line-height: 1.4;
  word-break: break-all;
}
.detail-item__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}
.detail-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.detail-cell__label {
  font-size: 11px;
  color: var(--color-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.detail-cell__value {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 卡片内转化率：与表格同色系，但饱和度更低更克制 */
.detail-cell--rate--high .detail-cell__value { color: var(--color-green); }
.detail-cell--rate--mid .detail-cell__value { color: var(--color-primary); }
.detail-cell--rate--low .detail-cell__value { color: var(--color-amber); }

.detail-pay {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid var(--color-border);
}
.detail-pay__row {
  display: grid;
  grid-template-columns: 56px 1fr 1fr;
  gap: 8px 12px;
  align-items: center;
  padding: 3px 0;
}
.detail-pay__channel {
  font-size: 12px;
  color: var(--color-text-secondary);
}
.detail-pay__value {
  font-size: 13px;
  color: var(--color-text);
  text-align: right;
}

@media (max-width: 640px) {
  .detail-item { padding: 12px 14px; }
  .detail-item__name { font-size: 13px; margin-bottom: 10px; }
  .detail-item__grid { grid-template-columns: repeat(4, 1fr); gap: 6px; }
  .detail-cell__label { font-size: 10px; }
  .detail-cell__value { font-size: 14px; }
  .detail-pay__row { grid-template-columns: 48px 1fr 1fr; gap: 6px 8px; }
  .detail-pay__channel { font-size: 11px; }
  .detail-pay__value { font-size: 12px; }
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

</style>



// WEBPACK FOOTER //
// src/view/hipc/Statistics.vue